/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { EuiDescribedFormGroup, EuiSpacer, EuiLink, EuiSkeletonText } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import { DocLink } from '@modules/doclinks'
import { networkSecurityUrl } from '@modules/network-security-pages/urls'
import { useListHostedTrafficFiltersQuery } from '@modules/network-security-lib/hooks/hosted'
import { useGetDeploymentQuery } from '@modules/cloud-lib/deployments/hooks'
import type { NormalizedTrafficFilterInfo, RulesetType } from '@modules/network-security-lib/types'

import TrafficFilterAssociationForm from '../TrafficFilterAssociationForm'
import TrafficFilterAssociationList from '../TrafficFilterAssociationList'
import ApplyButton from '../TrafficFilterAssociationList/ApplyButton'

const DeploymentNetworkSecurity: React.FunctionComponent = () => {
  const navigate = useNavigate()
  const { deploymentId } = useParams()

  const { data: deployment } = useGetDeploymentQuery(deploymentId, {
    enabled: Boolean(deploymentId),
  })
  const { data, isLoading } = useListHostedTrafficFiltersQuery()

  const [applyingRulesetType, setApplyingRulesetType] = useState<RulesetType | null>(null)

  if (!deployment) {
    return <EuiSkeletonText lines={3} />
  }

  const items: NormalizedTrafficFilterInfo[] = (data?.rulesets ?? [])
    .filter((ruleset) => ruleset.associations?.some(({ id }) => id === deployment.id))
    .map((ruleset) => ({
      type: 'hosted' as const,
      ruleset,
      associations: ruleset.associations ?? [],
    }))

  return (
    <Fragment>
      <EuiDescribedFormGroup
        ratio='third'
        fullWidth={true}
        title={
          <h3>
            <FormattedMessage
              id='deploymentNetworkSecurity.title'
              defaultMessage='Network security'
            />
          </h3>
        }
        description={
          <div>
            <FormattedMessage
              id='deploymentNetworkSecurity.description-line-1'
              defaultMessage='Protect your deployment by applying IP filter or private connection policies. Traffic matching any applied policy is allowed, and all other traffic is denied. <docLink>Learn more</docLink>'
              values={{
                docLink: (content) => (
                  <DocLink link='configureDeploymentTrafficFilters'>{content}</DocLink>
                ),
              }}
            />

            <EuiSpacer size='m' />

            <FormattedMessage
              id='deploymentNetworkSecurity.description-line-2'
              defaultMessage="New policies can be created in your organization's <link>Network security settings</link>."
              values={{
                link: (content) => (
                  <EuiLink
                    href={networkSecurityUrl()}
                    onClick={(e) => {
                      e.preventDefault()
                      navigate(networkSecurityUrl())
                    }}
                  >
                    {content}
                  </EuiLink>
                ),
              }}
            />
          </div>
        }
      >
        <ApplyButton
          size='s'
          onClick={(rulesetType) => {
            setApplyingRulesetType(rulesetType)
          }}
        />

        <EuiSpacer size='l' />

        <TrafficFilterAssociationList
          items={items}
          isLoading={isLoading}
          deployment={deployment}
          showSearch={false}
        />
      </EuiDescribedFormGroup>

      {renderFlyout()}
    </Fragment>
  )

  function renderFlyout() {
    if (applyingRulesetType && deployment) {
      return (
        <TrafficFilterAssociationForm
          onClose={() => {
            setApplyingRulesetType(null)
          }}
          rulesetType={applyingRulesetType}
          deployment={deployment}
        />
      )
    }

    return null
  }
}

export default DeploymentNetworkSecurity
